import React from 'react';
import { Clock, Users, Award, Globe, Target, CheckCircle, ArrowRight, Mail, Phone } from 'lucide-react';
import Banner from '../../../Components/Common/Banner';

const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <Icon className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
        </div>
        <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
);

const BenefitItem = ({ children }) => (
    <div className="flex items-start mb-3">
        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
        <span className="text-gray-700">{children}</span>
    </div>
);

const ClockStopper = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            <Banner pageName="CLOCK STOPPER" breadcrumbs="Home / Programs / Clock Stopper" />

            {/* Hero Section */}
            <section className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="flex items-center mb-6">
                                <Clock className="w-12 h-12 text-blue-600 mr-4" />
                                <h2 className="text-3xl font-bold text-gray-800">Transforming Futures</h2>
                            </div>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                At Clock Stopper, we offer unpaid internship opportunities designed to enhance the skills of fresh graduates entering the workforce. Our program focuses on providing hands-on experience and professional development in various fields. Participants benefit from mentorship, practical training, and exposure to real-world projects, preparing them for successful careers. As an E-Verify employer, we are dedicated to fostering talent and empowering young professionals to thrive in their chosen industries.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <div className="bg-blue-100 px-4 py-2 rounded-full">
                                    <span className="text-blue-800 font-medium">E-Verify Employer</span>
                                </div>
                                <div className="bg-green-100 px-4 py-2 rounded-full">
                                    <span className="text-green-800 font-medium">High Success Rate</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-2xl">
                            <div className="grid grid-cols-2 gap-6 text-center">
                                <div>
                                    <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-gray-800">500+</div>
                                    <div className="text-gray-600">Graduates Trained</div>
                                </div>
                                <div>
                                    <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-gray-800">85%</div>
                                    <div className="text-gray-600">Success Rate</div>
                                </div>
                                <div>
                                    <Globe className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-gray-800">15+</div>
                                    <div className="text-gray-600">Countries</div>
                                </div>
                                <div>
                                    <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-gray-800">100+</div>
                                    <div className="text-gray-600">Projects</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Who Should Take This Program */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">Who Should Take This Program?</h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            Clock Stopper is ideal for recent graduates seeking to bridge the gap between academic learning and practical application. Whether you're aiming to kickstart your career or enhance your skills, our program offers the platform to grow and succeed.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon={Users}
                            title="Recent Graduates"
                            description="Perfect for fresh graduates looking to gain practical experience and transition smoothly from academic to professional environments."
                        />
                        <FeatureCard
                            icon={Target}
                            title="Career Switchers"
                            description="Ideal for professionals looking to pivot their careers and gain experience in new fields with expert guidance."
                        />
                        <FeatureCard
                            icon={Globe}
                            title="International Students"
                            description="Designed to help international students understand American work culture while building valuable professional skills."
                        />
                    </div>
                </div>
            </section>

            {/* Why Choose Section */}
            <section className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">Why Choose Clock Stopper?</h2>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-3 rounded-lg mr-4 flex-shrink-0">
                                        <Award className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Expertise</h3>
                                        <p className="text-gray-600">As an E-Verify employer, we adhere to rigorous standards of compliance and integrity in employment practices.</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-3 rounded-lg mr-4 flex-shrink-0">
                                        <Users className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Mentorship</h3>
                                        <p className="text-gray-600">Benefit from personalized mentorship and guidance from seasoned professionals invested in your success.</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-3 rounded-lg mr-4 flex-shrink-0">
                                        <Target className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Real-World Exposure</h3>
                                        <p className="text-gray-600">Gain insights into industry dynamics and challenges through meaningful projects and hands-on experience.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 rounded-2xl">
                            <h3 className="text-2xl font-bold mb-6">Success Ratio</h3>
                            <p className="text-blue-100 mb-6">
                                Our program boasts a high success ratio, with a significant number of participants securing permanent positions or advancing in their careers post-internship.
                            </p>
                            <div className="bg-white/10 p-4 rounded-lg">
                                <div className="text-3xl font-bold mb-1">85%</div>
                                <div className="text-blue-200">of participants advance their careers</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* International Students Benefits */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">How International Students Benefit</h2>
                        <p className="text-lg text-gray-600">International students benefit immensely from Clock Stopper</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <div className="bg-green-100 p-3 rounded-lg w-fit mb-4">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Visa Compliance</h3>
                            <p className="text-gray-600">Gain practical experience that enhances employability and supports visa requirements.</p>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <div className="bg-purple-100 p-3 rounded-lg w-fit mb-4">
                                <Globe className="w-6 h-6 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Cultural Integration</h3>
                            <p className="text-gray-600">Acquire insights into American work culture and professional norms.</p>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <div className="bg-orange-100 p-3 rounded-lg w-fit mb-4">
                                <Target className="w-6 h-6 text-orange-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Career Advancement</h3>
                            <p className="text-gray-600">Develop skills valued in the global job market, preparing you for international career opportunities.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Points */}
            <section className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-800 mb-8">Key Program Features</h2>
                            <div className="space-y-4">
                                <BenefitItem>Hands-On Experience: Engaging participants in practical, skill-building activities</BenefitItem>
                                <BenefitItem>Professional Development: Providing mentorship and guidance for career growth</BenefitItem>
                                <BenefitItem>Real-World Exposure: Offering opportunities to work on meaningful projects</BenefitItem>
                                <BenefitItem>Career Readiness: Equipping graduates with essential skills for future success</BenefitItem>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl">
                            <h3 className="text-2xl font-bold text-gray-800 mb-6">Social Cause Behind the Program</h3>
                            <p className="text-gray-600 mb-6">
                                At Clock Stopper, we believe in empowering talent regardless of background. By offering unpaid internships, we contribute to societal progress by equipping individuals with the skills and confidence needed to thrive in their careers.
                            </p>
                            <div className="flex items-center text-blue-600 font-medium">
                                <span>Empowering Future Leaders</span>
                                {/* <ArrowRight className="w-4 h-4 ml-2" /> */}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-16 bg-blue-600 text-white">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Future?</h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Join Clock Stopper today and embark on a journey of growth, learning, and professional achievement.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        {/* <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center">
                            Visit Website
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </button> */}

                        <div className="flex gap-6 text-blue-100">
                            <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-2" />
                                <span>[+1 470 740 2777]</span>
                            </div>
                            <div className="flex items-center">
                                <Mail className="w-4 h-4 mr-2" />
                                <span>[info@techmentee.com]</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ClockStopper;
